import React from "react";
import { templatePath } from "../Templates";
import { transcriptNoteModalColumnText } from "./supportStyles";

const MetaBadge = ({ children, tone = "neutral" }) => {
  const toneClass =
    tone === "accent"
      ? "bg-purple-50 text-purple-800 border-purple-200"
      : tone === "success"
        ? "bg-green-50 text-green-800 border-green-200"
        : "bg-slate-100 text-slate-700 border-slate-200";

  return (
    <span
      className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${toneClass}`}
    >
      {children}
    </span>
  );
};

const TemplateMeta = ({ template }) => {
  if (!template || template.error) return null;

  return (
    <div className="flex flex-wrap gap-1.5">
      {template.version ? <MetaBadge>Version: {template.version}</MetaBadge> : null}
      {template.is_default ? <MetaBadge tone="success">Default</MetaBadge> : null}
      {template.type ? <MetaBadge tone="accent">{template.type}</MetaBadge> : null}
      {template.scope ? <MetaBadge>{template.scope}</MetaBadge> : null}
      {template.is_deleted ? <MetaBadge tone="accent">Deleted</MetaBadge> : null}
    </div>
  );
};

const TemplateBlock = ({ title, templateName, template }) => (
  <section className="rounded-md border border-gray-200 bg-white overflow-hidden">
    <div className="px-3 py-2 border-b border-gray-200 bg-slate-50 space-y-1.5">
      <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
      {templateName ? (
        <a
          href={templatePath(templateName)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[11px] font-mono text-ktx-purple hover:underline break-all"
          title="Open in Templates page"
        >
          {templateName}
        </a>
      ) : (
        <p className="text-[11px] text-gray-500">Not set on summarize job</p>
      )}
      <TemplateMeta template={template} />
    </div>
    <div className="p-3">
      {template?.error ? (
        <p className={`${transcriptNoteModalColumnText} text-red-600 text-sm`}>
          {template.error}
        </p>
      ) : template?.text ? (
        <pre
          className={`${transcriptNoteModalColumnText} text-sm bg-gray-50 border border-gray-200 rounded p-3 overflow-auto whitespace-pre-wrap text-gray-800 max-h-[28rem]`}
        >
          {template.text}
        </pre>
      ) : (
        <p className={`${transcriptNoteModalColumnText} text-sm text-gray-500`}>
          No template content available.
        </p>
      )}
    </div>
  </section>
);

const SummarizeTemplatesPanel = ({
  loading,
  error,
  sourceJobName,
  convPromptName,
  startingNoteName,
  convPromptTemplate,
  startingNoteTemplate,
}) => {
  if (loading) {
    return (
      <p className={`${transcriptNoteModalColumnText} text-gray-500`}>
        Loading templates from latest summarization job…
      </p>
    );
  }

  if (error && !convPromptTemplate && !startingNoteTemplate) {
    return (
      <p className={`${transcriptNoteModalColumnText} text-red-600 text-sm`}>{error}</p>
    );
  }

  return (
    <div className="space-y-4">
      {sourceJobName ? (
        <p className={`${transcriptNoteModalColumnText} text-xs text-gray-500`}>
          From summarize job:{" "}
          <span className="font-mono text-gray-700 break-all">{sourceJobName}</span>
        </p>
      ) : null}
      {error ? (
        <p className={`${transcriptNoteModalColumnText} text-amber-700 text-sm`}>{error}</p>
      ) : null}
      <TemplateBlock
        title="Conversation Prompt"
        templateName={convPromptName}
        template={convPromptTemplate}
      />
      <TemplateBlock
        title="Starting Note"
        templateName={startingNoteName}
        template={startingNoteTemplate}
      />
    </div>
  );
};

export default SummarizeTemplatesPanel;
